import { produce } from 'immer'
import {
  isValidEmail,
  isValidPhone,
  updatePhoneValueSearch,
} from '@sales-center/common'
import {
  IDateRangeFilter,
  IFilterSet,
  IMultiSelectFilter,
  INullableFilter,
  IOrderBy,
  ITernaryFilter,
  IValueRangeFilter,
  normalizeFilters,
  orderByToArray,
} from '@user/components/Filters'
import { IOpportunity } from './typings'

export interface OpportunityFilterSet extends IFilterSet<IOpportunity> {
  filters: {
    createdDate: IDateRangeFilter<IOpportunity>
    closedDate: IDateRangeFilter<IOpportunity>
    nextActionDate: IDateRangeFilter<IOpportunity>
    expectedClosingDate: IDateRangeFilter<IOpportunity>
    services: IMultiSelectFilter<IOpportunity>
    salesFunnelId: IMultiSelectFilter<IOpportunity>
    salesFunnelStageId: IMultiSelectFilter<IOpportunity>
    initialValue: IValueRangeFilter<IOpportunity>
    recurringValue: IValueRangeFilter<IOpportunity>
    totalValue: IValueRangeFilter<IOpportunity>
    createdBy: IMultiSelectFilter<IOpportunity>
    submittedBy: IMultiSelectFilter<IOpportunity>
    postalCode: IMultiSelectFilter<IOpportunity>
    billToPostalCode: IMultiSelectFilter<IOpportunity>
    locationTypeId: IMultiSelectFilter<IOpportunity>
    divisionId: IMultiSelectFilter<IOpportunity>
    closed: INullableFilter<IOpportunity>
    assignee: IMultiSelectFilter<IOpportunity>
    salesTeamId: IMultiSelectFilter<IOpportunity>
    salesTeamIds: IMultiSelectFilter<IOpportunity>
    sourceId: IMultiSelectFilter<IOpportunity>
    campaignId: IMultiSelectFilter<IOpportunity>
    soldServiceStatus: IMultiSelectFilter<IOpportunity>
    activityStatus: IMultiSelectFilter<IOpportunity>
    activityTypeId: IMultiSelectFilter<IOpportunity>
    type: IMultiSelectFilter<IOpportunity>
    importId: IMultiSelectFilter<IOpportunity>
    targetPests: IMultiSelectFilter<IOpportunity>
    leadCreatedTime: IDateRangeFilter<IOpportunity>
    agreementStatuses: IMultiSelectFilter<IOpportunity>
    hasPestPacLocation: ITernaryFilter<IOpportunity>
    closedLost: ITernaryFilter<IOpportunity>
    leadCreatedBy: IMultiSelectFilter<IOpportunity>
    knockStatusSettingId: IMultiSelectFilter<IOpportunity>
  }
}

//#region order by
export const defaultOrderBy = 'createdDate desc,id desc'

export const orderByOptions: IOrderBy[] = [
  {
    label: 'Created Date (Newest - Oldest)',
    value: 'createdDate desc,id desc',
  },
  { label: 'Created Date (Oldest - Newest)', value: 'createdDate asc,id asc' },
  { label: 'Next Action Date (Newest - Oldest)', value: 'nextActionDate desc' },
  { label: 'Next Action Date (Oldest - Newest)', value: 'nextActionDate asc' },
  {
    label: 'Expected Closing Date (Newest - Oldest)',
    value: 'expectedClosingDate desc',
  },
  {
    label: 'Expected Closing Date (Oldest - Newest)',
    value: 'expectedClosingDate asc',
  },
  { label: 'Contact Name (A-Z)', value: 'contactWithOpportunityName asc' },
  { label: 'Contact Name (Z-A)', value: 'contactWithOpportunityName desc' },
  { label: 'Total Value (High - Low)', value: 'totalValue desc' },
  { label: 'Total Value (Low - High)', value: 'totalValue asc' },
]

export const sortOptionArray = orderByToArray(orderByOptions)
//#endregion

// closedLost is set by the Sales Funnel "Lost" link and not shown in the filter drawer.
export const defaultFilters: OpportunityFilterSet = normalizeFilters<
  IOpportunity,
  OpportunityFilterSet
>({
  appliedCount: 0,
  normalizedFilters: [],
  orderBy: defaultOrderBy,
  filters: {
    createdDate: {
      propertyName: 'createdDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    leadCreatedTime: {
      propertyName: 'leadCreatedTime',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    closedDate: {
      propertyName: 'closedDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    nextActionDate: {
      propertyName: 'nextActionDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    expectedClosingDate: {
      propertyName: 'expectedClosingDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    services: {
      propertyName: 'services',
      type: 'multiSelect',
      propertyIsArray: true,
      propertyIsString: true,
      nestedPropertyName: 'id',
      value: { mode: 'any', options: [] },
    },
    activityStatus: {
      propertyName: 'activities',
      type: 'multiSelect',
      nestedPropertyName: 'status',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    activityTypeId: {
      propertyName: 'activities',
      type: 'multiSelect',
      nestedPropertyName: 'ActivityTypeId',
      propertyIsArray: true,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesFunnelId: {
      propertyName: 'salesFunnelId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesFunnelStageId: {
      propertyName: 'salesFunnelStageId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    initialValue: {
      propertyName: 'initialValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    recurringValue: {
      propertyName: 'recurringValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    totalValue: {
      propertyName: 'totalValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    createdBy: {
      propertyName: 'createdBy',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      nestedPropertyName: 'id',
      value: { mode: 'any', options: [] },
    },
    submittedBy: {
      propertyName: 'submittedBy',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    assignee: {
      propertyName: 'assignee',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      nestedPropertyName: 'id',
      value: { mode: 'any', options: [] },
    },
    postalCode: {
      propertyName: 'postalCode',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      startsWith: true,
      value: { mode: 'any', options: [] },
    },
    billToPostalCode: {
      propertyName: 'billToPostalCode',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      startsWith: true,
      value: { mode: 'any', options: [] },
    },
    locationTypeId: {
      propertyName: 'locationTypeId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    divisionId: {
      propertyName: 'divisionId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    closed: {
      propertyName: 'closedDate',
      type: 'nullable',
      value: 'null',
    },
    salesTeamId: {
      propertyName: 'salesTeamId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesTeamIds: {
      propertyName: 'salesTeamId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    sourceId: {
      propertyName: 'sourceId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    campaignId: {
      propertyName: 'campaignId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    soldServiceStatus: {
      propertyName: 'soldServiceStatus',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    type: {
      propertyName: 'type',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    importId: {
      propertyName: 'importId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    targetPests: {
      propertyName: 'targetPests',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    agreementStatuses: {
      propertyName: 'agreementStatuses',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      hideAllMode: true,
      value: { mode: 'any', options: [] },
    },
    hasPestPacLocation: {
      propertyName: 'hasPestPacLocation',
      type: 'ternary',
      value: 'empty',
    },
    closedLost: {
      propertyName: 'closedLost',
      type: 'ternary',
      value: 'empty',
    },
    leadCreatedBy: {
      propertyName: 'leadCreatedBy',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    knockStatusSettingId: {
      propertyName: 'knockStatusSettingId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
  },
  search: {
    value: '',
    placeholder: 'Name, email, phone, address, location',
    searchFields: [
      {
        propertyNames: ['opportunityName'],
        matchType: 'contains',
      },
      {
        propertyNames: [
          'contactName',
          'billToContactName',
          'company',
          'primaryContactSearchName',
        ],
        matchType: 'contains',
      },
      {
        propertyNames: ['externalLocationCode'],
        matchType: 'eq',
        include: /^[\d]+$/,
      },
      {
        propertyNames: [
          'phone',
          'mobilePhone',
          'billToPhone',
          'billToMobilePhone',
          'primaryContactSearchPhone',
          'primaryContactSearchMobile',
        ],
        matchType: 'eq',
        include: isValidPhone,
        formatter: updatePhoneValueSearch,
      },
      {
        propertyNames: [
          'emailAddress',
          'billToEmail',
          'primaryContactSearchPrimaryEmail',
        ],
        matchType: 'eq',
        include: isValidEmail,
      },
      {
        propertyNames: [
          'address',
          'billToAddress',
          'primaryContactSearchAddress',
        ],
        matchType: 'contains',
      },
    ],
  },
})

export const clearedFilters = produce(defaultFilters, (draft) => {
  draft.filters.closed.value = 'empty'
})
